import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartHandshake, Building2, Users, Server, Repeat, Boxes,
  Check, ArrowRight, Loader2, Send,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { postPartner } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

// Les 2 façons de développer son activité avec Umbo.
const MODELS = [
  {
    icon: HeartHandshake,
    name: "Partenaire Umbo",
    accroche: "Développez votre portefeuille avec une marque, une offre et une infrastructure déjà opérationnelles.",
    points: [
      "Vous prospectez et développez votre propre portefeuille de clients",
      "Vous présentez et commercialisez les offres Umbo",
      "Vous conduisez la relation commerciale jusqu'à la signature",
      "Umbo vous accompagne sur les sujets techniques avant la vente",
      "Umbo assure ensuite le déploiement, l'exploitation et le support",
      "Vous développez des revenus récurrents sur votre portefeuille",
      "Supports commerciaux et formation aux offres fournis",
    ],
    cta: "Devenir partenaire Umbo",
    value: "Partenaire Umbo",
  },
  {
    icon: Building2,
    name: "Partenaire en marque blanche",
    accroche: "Votre marque devant. Notre infrastructure derrière.",
    points: [
      "Vous commercialisez les services sous votre propre marque",
      "Vous prospectez, vendez et gérez votre relation client",
      "Vous définissez librement votre positionnement commercial",
      "Vous construisez votre propre marge",
      "Umbo fournit l'infrastructure et les services techniques",
      "Umbo intervient en support technique lorsque nécessaire",
      "Idéal pour sociétés IT, télécoms, consultants, intégrateurs ou entrepreneurs disposant déjà d'un réseau professionnel",
    ],
    cta: "Découvrir la marque blanche",
    value: "Marque blanche",
  },
];

// Qui fait quoi — répartition claire des rôles.
const YOU_DOES = [
  "Prospection",
  "Prise de rendez-vous",
  "Qualification du besoin",
  "Présentation de l'offre",
  "Négociation commerciale",
  "Signature",
  "Relation commerciale client",
];
const UMBO_DOES = [
  "Accompagnement avant-vente technique",
  "Cybersécurité",
  "Infrastructure IT",
  "Télécom",
  "Déploiement",
  "Supervision",
  "Support",
  "Sauvegarde et PRA",
  "Exploitation quotidienne",
];

// Profils recherchés.
const PROFILES = [
  "Commerciaux indépendants B2B",
  "Entrepreneurs disposant d'un réseau de dirigeants",
  "Consultants IT",
  "Intégrateurs et prestataires informatiques",
  "Professionnels des télécoms",
  "Agences numériques",
  "Entrepreneurs souhaitant développer une activité B2B récurrente",
];

// Pourquoi le modèle est intéressant.
const BENEFITS = [
  {
    icon: Repeat,
    title: "Revenus récurrents",
    text: "Construire progressivement un portefeuille générant du revenu chaque mois.",
  },
  {
    icon: Server,
    title: "Pas d'infrastructure à créer",
    text: "Les solutions techniques, fournisseurs et procédures sont déjà en place.",
  },
  {
    icon: Boxes,
    title: "Offre complète",
    text: "Un seul partenaire peut répondre à une grande partie des besoins IT, cyber et télécom de ses clients.",
  },
  {
    icon: HeartHandshake,
    title: "Accompagnement",
    text: "Umbo apporte l'expertise technique nécessaire pour sécuriser les projets et les ventes.",
  },
];

const MODELE_OPTIONS = ["Partenaire Umbo", "Marque blanche", "Je ne sais pas encore"];
const PORTEFEUILLE_OPTIONS = ["Oui", "Non", "En cours de développement"];

export default function Partenaires() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    prenom: "", nom: "", societe: "", email: "", telephone: "",
    secteur: "", activite: "", experience: "", modele: "", portefeuille: "", message: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // Défile vers une section ; préremplit éventuellement le modèle souhaité du formulaire.
  const goTo = (id, modele) => {
    if (modele) setForm((f) => ({ ...f, modele }));
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.email) {
      toast.error("Merci de renseigner votre prénom, votre nom et votre email.");
      return;
    }
    setLoading(true);
    try {
      await postPartner(form);
      trackEvent("generate_lead", { method: "partenaire" });
      navigate("/merci");
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler au 03 20 49 02 04.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Devenir partenaire Umbo | Cybersécurité, IT & Télécom"
        description="Développez votre activité B2B avec Umbo, sous notre marque ou en marque blanche. Vous développez les clients, Umbo opère la technique."
        path="/partenaires"
        suffix={false}
        breadcrumb={[
          { name: "Accueil", path: "/" },
          { name: "Partenaires", path: "/partenaires" },
        ]}
      />

      {/* HERO */}
      <section className="bg-navy grain relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-[-8%] w-[42rem] h-[42rem] rounded-full bg-amber-500/10 blur-[150px]" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 left-[-10%] w-[34rem] h-[34rem] rounded-full bg-[#0b264e]/60 blur-[130px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Devenir partenaire</span></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-white text-balance">
              Entreprenez. Vendez. Umbo s'occupe de la technique.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
              Développez votre propre portefeuille de clients professionnels en vous appuyant sur une offre complète
              de cybersécurité, informatique et télécom déjà opérationnelle. Vous pilotez la relation commerciale,
              Umbo prend en charge la technique.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <CTAButton size="lg" withArrow onClick={() => goTo("candidature")} data-testid="hero-cta-primary">
                Devenir partenaire
              </CTAButton>
              <CTAButton size="lg" variant="ghostLight" onClick={() => goTo("modeles")} data-testid="hero-cta-secondary">
                Découvrir les modèles
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* POSITIONNEMENT */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Une activité à construire</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">
              Développez votre activité avec Umbo.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Vous avez un réseau professionnel et savez vendre aux entreprises ? Appuyez-vous sur notre
              infrastructure, nos services et notre expertise pour développer votre propre portefeuille de clients.
            </p>
          </Reveal>
        </div>
      </section>

      {/* DEUX MODÈLES */}
      <section id="modeles" className="scroll-mt-24 bg-[#f8fafc] py-24 md:py-28 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Deux modèles</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Deux façons de développer votre activité avec Umbo.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-lg text-slate-600 leading-relaxed">Dans les deux cas, vous menez la relation commerciale de la prospection à la signature. Umbo prend en charge toute la partie technique.</p></Reveal>
          </div>
          <Stagger className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {MODELS.map((m) => (
              <StaggerItem key={m.name} className="flex flex-col bg-white border border-slate-200 rounded-2xl p-8 hover:border-navy/30 hover:shadow-lg transition-[border-color,box-shadow] duration-300">
                <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center mb-5"><m.icon className="w-6 h-6 text-amber-500" /></div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-navy">{m.name}</h3>
                <p className="mt-2 text-navy/80 font-medium leading-snug">{m.accroche}</p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-amber-500 mt-1 shrink-0" />
                      <span className="text-sm text-slate-600 leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => goTo("candidature", m.value)}
                  className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group self-start"
                  data-testid={`model-cta-${m.value}`}
                >
                  {m.cta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* QUI FAIT QUOI ? */}
      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-16 left-[-8%] w-[38rem] h-[38rem] rounded-full bg-amber-500/10 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Qui fait quoi ?</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">Une répartition claire des rôles.</h2></Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* VOUS */}
            <Reveal>
              <div className="h-full rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-amber-500/15 grid place-items-center"><Users className="w-5 h-5 text-amber-500" /></div>
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-500">Vous</span>
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight text-white">Vous développez le business</h3>
                <ul className="mt-6 space-y-3">
                  {YOU_DOES.map((y) => (
                    <li key={y} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-slate-200 leading-relaxed">{y}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            {/* UMBO */}
            <Reveal delay={0.08}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-white/[0.06] grid place-items-center"><Server className="w-5 h-5 text-amber-500" /></div>
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-300">Umbo</span>
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight text-white">Nous opérons la technique</h3>
                <ul className="mt-6 space-y-3">
                  {UMBO_DOES.map((u) => (
                    <li key={u} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-slate-200 leading-relaxed">{u}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10">
              <p className="text-lg text-slate-200 leading-relaxed">
                Vous restez concentré sur le développement commercial.
                <span className="text-white font-medium"> Umbo vous évite de devoir construire et maintenir toute l'infrastructure technique nécessaire pour délivrer les services.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROFILS RECHERCHÉS */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Profils recherchés</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Des profils commerciaux, pas des experts techniques.</h2></Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 rounded-2xl border border-slate-200 bg-[#f8fafc] p-7">
                <p className="text-lg text-slate-700 leading-relaxed">
                  Nous recherchons avant tout des entrepreneurs capables de prospecter, créer une relation de confiance
                  avec des dirigeants de TPE et conclure des affaires.
                  <span className="text-navy font-semibold"> La maîtrise technique de la cybersécurité ou de l'informatique n'est pas indispensable</span> :
                  Umbo est là pour apporter cette expertise.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Stagger className="grid sm:grid-cols-2 gap-4">
              {PROFILES.map((p) => (
                <StaggerItem key={p} className="flex items-start gap-3 rounded-xl border border-slate-200 p-5 hover:border-navy/30 transition-colors duration-300">
                  <div className="w-8 h-8 rounded-lg bg-navy grid place-items-center shrink-0"><Check className="w-4 h-4 text-amber-500" /></div>
                  <span className="text-slate-700 leading-snug">{p}</span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* POURQUOI LE MODÈLE EST INTÉRESSANT */}
      <section className="bg-[#f8fafc] py-24 md:py-28 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Pourquoi ce modèle</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Un modèle pensé pour durer.</h2></Reveal>
          </div>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b) => (
              <StaggerItem key={b.title} className="bg-white border border-slate-200 rounded-2xl p-8 h-full">
                <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center mb-5"><b.icon className="w-6 h-6 text-amber-500" /></div>
                <h3 className="font-display text-lg font-semibold tracking-tight text-navy leading-snug">{b.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{b.text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-navy-dark grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute -bottom-16 right-[-6%] w-[36rem] h-[36rem] rounded-full bg-amber-500/10 blur-[130px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">
              Vous avez le réseau. Nous avons l'infrastructure.
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Développez votre propre portefeuille de clients avec une offre complète de services IT, cybersécurité
              et télécom, sans avoir à construire toute l'infrastructure technique derrière.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={() => goTo("candidature")} data-testid="final-cta">
              Candidater pour devenir partenaire
            </CTAButton>
          </Reveal>
        </div>
      </section>

      {/* FORMULAIRE PARTENAIRE */}
      <section id="candidature" className="scroll-mt-24 bg-white py-24 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-center mb-12">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Candidature</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Candidater pour devenir partenaire.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-lg text-slate-600 leading-relaxed">Quelques informations pour préparer notre échange. Nous revenons vers vous rapidement.</p></Reveal>
          </div>
          <Reveal delay={0.12}>
            <form onSubmit={submit} className="border border-slate-200 rounded-2xl p-8 sm:p-10 space-y-5" data-testid="partner-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5"><Label htmlFor="p-prenom">Prénom *</Label><Input id="p-prenom" value={form.prenom} onChange={set("prenom")} placeholder="Votre prénom" data-testid="partner-prenom" /></div>
                <div className="space-y-1.5"><Label htmlFor="p-nom">Nom *</Label><Input id="p-nom" value={form.nom} onChange={set("nom")} placeholder="Votre nom" data-testid="partner-nom" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5"><Label htmlFor="p-societe">Société</Label><Input id="p-societe" value={form.societe} onChange={set("societe")} placeholder="Nom de votre société" data-testid="partner-societe" /></div>
                <div className="space-y-1.5"><Label htmlFor="p-email">Email *</Label><Input id="p-email" type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.fr" data-testid="partner-email" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5"><Label htmlFor="p-tel">Téléphone</Label><Input id="p-tel" value={form.telephone} onChange={set("telephone")} placeholder="06 XX XX XX XX" data-testid="partner-telephone" /></div>
                <div className="space-y-1.5"><Label htmlFor="p-secteur">Ville / secteur géographique</Label><Input id="p-secteur" value={form.secteur} onChange={set("secteur")} placeholder="Ex. Lille, Hauts-de-France" data-testid="partner-secteur" /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="p-activite">Activité actuelle</Label><Input id="p-activite" value={form.activite} onChange={set("activite")} placeholder="Ex. consultant IT, agence web, commercial B2B…" data-testid="partner-activite" /></div>
              <div className="space-y-1.5"><Label htmlFor="p-exp">Quelle est votre expérience commerciale B2B ?</Label><Textarea id="p-exp" rows={2} value={form.experience} onChange={set("experience")} placeholder="Ex. 5 ans en vente de services aux entreprises, indépendant depuis 2 ans…" data-testid="partner-experience" /></div>

              <div className="space-y-1.5">
                <Label>Modèle souhaité</Label>
                <Select value={form.modele} onValueChange={set("modele")}>
                  <SelectTrigger data-testid="partner-modele"><SelectValue placeholder="Sélectionnez un modèle" /></SelectTrigger>
                  <SelectContent>{MODELE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="space-y-2.5">
                <Label>Disposez-vous déjà d'un réseau ou portefeuille de clients professionnels ?</Label>
                <RadioGroup value={form.portefeuille} onValueChange={set("portefeuille")} className="grid sm:grid-cols-3 gap-3" data-testid="partner-portefeuille">
                  {PORTEFEUILLE_OPTIONS.map((opt, i) => (
                    <label key={opt} htmlFor={`pf-${i}`} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:border-navy/40 transition-colors has-[:checked]:border-navy has-[:checked]:bg-[#f8fafc]">
                      <RadioGroupItem value={opt} id={`pf-${i}`} />
                      <span className="text-sm text-navy font-medium">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-1.5"><Label htmlFor="p-msg">Message libre</Label><Textarea id="p-msg" rows={4} value={form.message} onChange={set("message")} placeholder="Parlez-nous de votre réseau, de votre projet, de vos questions…" data-testid="partner-message" /></div>

              <CTAButton type="submit" size="lg" className="w-full" disabled={loading} data-testid="partner-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Envoyer ma candidature</>}
              </CTAButton>
              <p className="text-center text-sm text-slate-400">Sans engagement · Réponse rapide</p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
